/**
 * Guest viewing slot display and reservation helpers.
 */

import { formatInquiryDate, formatDateTime } from './bookingInquiryDisplay';

/** Fields safe to render on guest /bookings cards. */
export const GUEST_SLOT_PUBLIC_FIELDS = new Set([
  'id',
  'start_time',
  'end_time',
  'timezone',
  'capacity',
  'reserved_count',
  'status',
  'has_availability',
  'location_type',
  'meeting_location',
]);

const PRIVATE_SLOT_FIELD_PREFIXES = [
  'notes_internal',
  'calcom_',
  'owner_email',
  'guest_email',
  'user_email',
  'phone',
  'address',
];

export function isPrivateSlotField(key) {
  const normalized = String(key || '').toLowerCase();
  if (GUEST_SLOT_PUBLIC_FIELDS.has(normalized)) {
    return false;
  }
  return PRIVATE_SLOT_FIELD_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(prefix),
  );
}

export function sanitizeGuestSlotForDisplay(slot) {
  if (!slot || typeof slot !== 'object') {
    return {};
  }
  return Object.fromEntries(
    Object.entries(slot).filter(([key]) => !isPrivateSlotField(key) && GUEST_SLOT_PUBLIC_FIELDS.has(key)),
  );
}

export function remainingSpots(slot) {
  const capacity = Number(slot?.capacity);
  const reserved = Number(slot?.reserved_count ?? 0);
  if (!Number.isFinite(capacity) || capacity < 1) {
    return null;
  }
  const used = Number.isFinite(reserved) ? reserved : 0;
  return Math.max(0, capacity - used);
}

export function formatSlotDate(isoValue) {
  return formatInquiryDate(isoValue) || '';
}

export function formatSlotTimeRange(startIso, endIso) {
  const start = formatDateTime(startIso);
  const end = formatDateTime(endIso);
  if (start && end) {
    return `${start} – ${end}`;
  }
  return start || end || '';
}

export function isViewingAlreadyReserved(viewingState) {
  const state = String(viewingState || '').toLowerCase();
  return state === 'viewing_reserved' || state === 'viewing_confirmed';
}

export function canShowGuestReserveActions(viewingState) {
  return !isViewingAlreadyReserved(viewingState);
}

export function mapReserveConflictMessage(detail, t) {
  const text = String(detail || '').toLowerCase();
  if (
    text.includes('active viewing reservation already exists')
    || text.includes('already exists for this inquiry')
  ) {
    return t(
      'booking.viewing_conflict_duplicate',
      'You already have a viewing reservation for this inquiry.',
    );
  }
  if (text.includes('full') || text.includes('no longer available')) {
    return t(
      'booking.viewing_conflict_unavailable',
      'This viewing slot is no longer available.',
    );
  }
  return null;
}

export function resolveGuestViewingSectionState({
  slots,
  loading,
  error,
  viewingState,
}) {
  if (isViewingAlreadyReserved(viewingState)) {
    return { mode: 'reserved' };
  }
  if (loading) {
    return { mode: 'loading' };
  }
  if (error) {
    return { mode: 'error' };
  }
  if (!slots?.length) {
    return { mode: 'empty' };
  }
  return { mode: 'slots' };
}
